import React, { useEffect, useState, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Input from "shared/components/FormElements/Input";
import CustomSelect from "shared/components/FormElements/Select";
import NotFound from "shared/components/UIElements/NotFound";
import MapSinglePoint from "components/shared/MapSinglePoint";
import FormFooter from "shared/components/FormElements/FormFooter";
import DeleteModal from "components/shared/DeleteModal";
import ItemGenerated from "components/shared/ItemGenerated";
import LoginCodeField from "features/users/components/LoginCodeField";
import { AuthContext } from "shared/context/auth-context";

import { VALIDATOR_REQUIRE, VALIDATOR_NONE } from "shared/util/validators";

import InputPhoneNumber from "components/ui/input/InputPhoneNumber";
import { getCurrentLanguage } from "shared/utility/lang";

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { StoreContext } from "shared/context/store-context";
import { USER_TYPES, SCHOOL_TYPES } from "@routebot/constants";

const ParentItem = ({
  itemId,
  onClose,
  onCreated,
  isNewItem,
  gotoItem,
  DeleteItem,
  UpdateItem,
  InsertItem,
  inModal,
}) => {
  //standard code
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // context
  const { users, schools } = useContext(StoreContext);

  const parentSchoolOptions = schools
    .filter((school) => school.type === SCHOOL_TYPES.SCHOOL)
    .map((school) => ({
      label: school.name,
      value: school.id.toString(),
    }));

  // item info
  const itemType = "user";
  const itemTextType = "parent";
  const endPointUpdate = "users";
  const endPointDelete = "users";
  const endPointCreate = "users";

  // item special states
  const [markerLocation, setMarkerLocation] = useState();
  const [userAddress, setUserAddress] = useState();
  const [addressInSync, setAddressInSync] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState(() => auth?.countryCode || "TR");
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(true);

  // item special functions
  const mapDataChangeHandler = (address, location, inSync) => {
    setMarkerLocation(location);
    setUserAddress(address);
    setAddressInSync(inSync);
    inputHandler("address", address, true);
  };

  const phoneNumberChangeHandler = (phoneNumber, countryCode, isValid) => {
    setPhoneNumber(phoneNumber || "");
    setCountryCode(countryCode);
    setIsPhoneNumberValid(isValid);
  };

  // formstates
  const [formState, inputHandler] = useForm(
    {
      //inputs
      name: { value: "", isValid: true },

      email: { value: "", isValid: true },
      address: { value: null, isValid: true },
      identity: { value: "", isValid: true },
      school: { value: parentSchoolOptions.length === 1 ? parentSchoolOptions[0].value : null, isValid: true },
    },
    true,
  );

  // formstates fetch
  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    try {
      const currentUser = users.find((user) => user.id === itemId);
      setLoadedItem(currentUser);
      if (currentUser?.location && currentUser?.location.lat && currentUser?.location.lng) {
        setMarkerLocation(currentUser?.location);
        setAddressInSync(true);
      } else {
        setMarkerLocation(null);
        setAddressInSync(false);
      }

      setPhoneNumber(currentUser?.phonenumber || "");
      setCountryCode(currentUser?.countryCode || auth?.countryCode || "TR");
      setUserAddress(currentUser?.address);
    } catch (err) {}
    setIsItemLoaded(true);
  }, [users, itemId]);

  // standard code
  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  // item update
  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();

    try {
      const infoToBeSent = {
        // Generic fields
        usertype: USER_TYPES.SCHOOL_PARENT,
        name: formState.inputs.name.value.toString().trim(),
        phonenumber: phoneNumber,
        countryCode: countryCode,
        email: formState.inputs.email.value,
        address: userAddress,
        identity: formState.inputs.identity.value,
        school: formState.inputs.school.value,
      };

      if (markerLocation) {
        infoToBeSent.location = { lat: markerLocation?.lat || 1.1, lng: markerLocation?.lng || 1.1 };
      }

      const endpoint = isNewItem
        ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
        : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

      const postType = isNewItem ? "POST" : "PATCH";
      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);
        } else {
          UpdateItem(responseData[itemType]);
          window.toast.success(t("updateSuccess"));
        }
      }

      if (inModal) {
        onCreated(responseData?.[itemType]);
        onClose();
      }
    } catch (err) {
      if (err?.code) {
        window.toast.error(t("httpCodes." + err.code));
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  };

  //standard code
  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item={itemTextType}></NotFound>;
  }

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemTextType}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }

  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemTextType}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {isItemLoaded && (
        <form className="form-container">
          <CustomSelect
            options={parentSchoolOptions}
            id="school"
            onInput={inputHandler}
            label={t("school") + " *"}
            initialValue={
              parentSchoolOptions.find((option) => option.value.toString() === loadedItem?.school.toString())
                ? parentSchoolOptions.find((option) => option.value.toString() === loadedItem?.school.toString())
                : parentSchoolOptions.length === 1
                  ? parentSchoolOptions[0]
                  : null
            }
            initialValid={isNewItem ? false : true}
          ></CustomSelect>

          <div className="horizontal-flex">
            <Input
              id="name"
              element="input"
              type="text"
              label={t("name") + " *"}
              validators={[VALIDATOR_REQUIRE()]}
              errorText={t("requireField", { item: t("name") })}
              initialValue={loadedItem?.name}
              initialValid={isNewItem ? false : true}
              onInput={inputHandler}
            />
            <InputPhoneNumber
              id="phonenumber"
              label={t("phone")}
              phoneNumber={phoneNumber}
              countryCode={countryCode}
              onChange={phoneNumberChangeHandler}
              errorText={t("phoneNumberorEmpty")}
              language={getCurrentLanguage()}
              hideLabel={auth.countryCode === countryCode}
            />
          </div>

          <MapSinglePoint
            id="userItemMap"
            label={formState.inputs.name.value}
            initialAddress={userAddress || ""}
            initialAddressInSync={addressInSync}
            initialLocation={markerLocation}
            onInput={inputHandler}
            onMapDataChange={mapDataChangeHandler}
            isRequired={false}
            isOnlyAddress={true}
            title={t("contactAddress")}
          ></MapSinglePoint>
          <div className="horizontal-flex">
            <Input
              id="email"
              element="input"
              type="text"
              label={t("email")}
              validators={[VALIDATOR_NONE()]}
              errorText={t("requireField", { item: t("email") })}
              onInput={inputHandler}
              initialValue={loadedItem?.email || ""}
              initialValid={true}
            />
            <Input
              id="identity"
              element="input"
              type="text"
              label={t("identityNumber")}
              validators={[VALIDATOR_NONE()]}
              errorText={t("requireField", { item: t("identityNumber") })}
              initialValue={loadedItem?.identity || ""}
              initialValid={true}
              onInput={inputHandler}
            />
            {!isNewItem && (
              <LoginCodeField loginCode={loadedItem?.loginCode} phoneNumber={phoneNumber} countryCode={countryCode} />
            )}
          </div>

          <FormFooter
            isNewItem={isNewItem}
            onUpdate={itemUpdateSubmitHandler}
            onDelete={() => setShowConfirmModal(true)}
            isUpdating={isLoading}
            // style={{ paddingBottom: "0px" }}
            disabled={!formState.isValid || !formState.inputs.school.value || !isPhoneNumberValid || isLoading}
          />
        </form>
      )}
    </React.Fragment>
  );
};

export default ParentItem;
